import { useTranslation } from 'react-i18next';
import { Button, useSnackbar } from '@c2fo/liquidity';
import emptystatements from '@/enterprise/assets/emptystatementsgrey.svg?url';
import DownloadMenu from '@/enterprise/components/DownloadMenu';
import EmptyRender from '@/enterprise/components/EmptyRender';
import NoDataState from '@/enterprise/components/NoDataState';
import {
  Table,
  TableBody,
  TableCell,
  TableDisplay,
  TableHead,
  TableHeader,
  TableRow,
} from '@/enterprise/components/Table';
import { downloadInstantPayStatement, InstantPayStatementType } from '@/enterprise/data/downloadInstantPayStatement';
import { SubstitutionRecord } from '@/enterprise/data/useSubstitutionRecords';
import useLocaleFormat from '@/utils/useLocaleFormat';

export const COLUMN_COUNT = 6;

interface SubstitutionRecordsTableProps {
  records: SubstitutionRecord[];
  /** Blurs the current rows while a filter change loads. */
  loading: boolean;
  /** Whether any filter is applied, which picks the empty state. */
  hasFiltersApplied: boolean;
  onAdjustFilters: () => void;
}

export const SubstitutionRecordsTableHeader = () => {
  const { t } = useTranslation();

  return (
    <TableHeader>
      <TableRow>
        <TableHead>{t('instantPay.substitutions.substitutionDate')}</TableHead>
        <TableHead>{t('instantPay.substitutions.customer')}</TableHead>
        <TableHead>{t('instantPay.substitutions.division')}</TableHead>
        <TableHead className="text-right">{t('instantPay.substitutions.invoicesSubstituted')}</TableHead>
        <TableHead className="text-right">{t('instantPay.substitutions.substituteAmount')}</TableHead>
        <TableHead className="text-right">{t('instantPay.substitutions.downloadRecord')}</TableHead>
      </TableRow>
    </TableHeader>
  );
};

const SubstitutionRecordsEmptyState = ({
  hasFiltersApplied,
  onAdjustFilters,
}: Pick<SubstitutionRecordsTableProps, 'hasFiltersApplied' | 'onAdjustFilters'>) => {
  const { t } = useTranslation();

  return (
    <NoDataState
      className="py-8"
      icon={<img src={emptystatements} alt="" className="h-28 w-28" />}
      title={t('instantPay.substitutions.noRecords')}
      message={
        hasFiltersApplied
          ? t('instantPay.substitutions.noMatchesDescription')
          : t('instantPay.substitutions.noRecordsDescription')
      }
      action={
        hasFiltersApplied ? (
          <Button onClick={onAdjustFilters} variant="tertiary">
            {t('core.adjustFilters')}
          </Button>
        ) : undefined
      }
    />
  );
};

const SubstitutionRecordsTable = ({
  records,
  loading,
  hasFiltersApplied,
  onAdjustFilters,
}: SubstitutionRecordsTableProps) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const { asCurrency, asDateStringText, asNumber } = useLocaleFormat();

  const handleDownload = async (record: SubstitutionRecord, type: InstantPayStatementType): Promise<void> => {
    try {
      await downloadInstantPayStatement({
        makerOrganizationUuid: record.makerOrganizationUuid,
        statementId: record.statementId,
        type,
      });
    } catch {
      showSnackbar({ message: t('core.downloadError') });
    }
  };

  // PDF is left out until platform can produce it; DownloadMenu takes more formats per group as they arrive.
  const downloadGroups = [
    {
      id: 'substitution' as const,
      label: t('instantPay.substitutions.substitutionFile'),
      formats: [{ id: 'csv' as const, label: t('maker.csv') }],
    },
    {
      id: 'substitution-summary' as const,
      label: t('instantPay.substitutions.substitutionSummaryFile'),
      formats: [{ id: 'csv' as const, label: t('maker.csv') }],
    },
  ];

  return (
    <div className="overflow-auto">
      <Table>
        <SubstitutionRecordsTableHeader />
        <TableBody loading={loading} aria-busy={loading}>
          {records.length > 0 ? (
            records.map((record) => (
              <TableRow key={`${record.makerOrganizationUuid}-${record.uuid}`}>
                <TableCell>{asDateStringText({ date: record.instantPayDate })}</TableCell>
                <TableCell>{record.customerName}</TableCell>
                <TableCell>
                  <EmptyRender condition={!!record.divisionName}>{record.divisionName}</EmptyRender>
                </TableCell>
                <TableCell className="text-right">{asNumber({ value: record.invoiceCount })}</TableCell>
                <TableCell className="text-right">{asCurrency(record.totalInvoiceAmount, record.currency)}</TableCell>
                <TableCell>
                  <div className="flex justify-end">
                    <DownloadMenu
                      name={t('instantPay.substitutions.downloadRecord')}
                      groups={downloadGroups}
                      onSelect={(type) => void handleDownload(record, type)}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableDisplay colSpan={COLUMN_COUNT}>
              <SubstitutionRecordsEmptyState hasFiltersApplied={hasFiltersApplied} onAdjustFilters={onAdjustFilters} />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default SubstitutionRecordsTable;
